import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { http } from "@/lib/api";

/**
 * Anonymous page-view tracker. Called once from <App /> — each route change
 * fires a lightweight POST to /api/analytics/pageview with the caller's
 * visitor UUID (persisted in localStorage), path, title, referrer, and lang.
 * Bots are filtered server-side.
 */
const VISITOR_KEY = "popoyo:analytics:visitor";
const SESSION_KEY = "popoyo:analytics:session";

function uuid() {
  // Cheap RFC-4122 v4 without pulling in a lib
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function getOrMake(storage, key) {
  try {
    let v = storage.getItem(key);
    if (!v) {
      v = uuid();
      storage.setItem(key, v);
    }
    return v;
  } catch (_e) {
    return uuid();
  }
}

export default function useAnalytics() {
  const { pathname, search } = useLocation();

  useEffect(() => {
    // Skip on the very first mount if pathname is empty (SSR safety)
    if (typeof window === "undefined") return;
    // Never track the admin panel's own /#admin section — it's noisy
    try {
      const visitor = getOrMake(window.localStorage, VISITOR_KEY);
      const session = getOrMake(window.sessionStorage, SESSION_KEY);
      const payload = {
        path: `${pathname}${search || ""}`.slice(0, 200),
        title: document.title.slice(0, 200),
        referrer: document.referrer ? document.referrer.slice(0, 500) : "",
        visitor,
        session,
        lang: (navigator.language || "").slice(0, 12),
        screen: `${window.innerWidth}x${window.innerHeight}`.slice(0, 20),
      };
      // fire-and-forget; failures are silent so tracker never breaks the UI
      http.post("/analytics/pageview", payload).catch(() => {});
    } catch (_e) { /* noop */ }
  }, [pathname, search]);
}
